import React from 'react';
import { Grid, GridRow, GridCol } from '@naf/grid';
import styled from '@emotion/styled';
import { NavCard } from '../../simba-cards';
import { NavigationLinksType } from '../../../../../types/navigationLinksType';
import { ExternalLinkType } from '../../../../../types/externalLinkType';
import { CtaInternalLinkType } from '../../../../../types/internalLinkType';
import { useDocumentUrlWithNode } from '../../../hooks/Documents/useDocumentUrl';
import { NavCardLink } from '../../styled-link/StyledLink';
import { useTrackPromotion } from '../../../hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '../../../utils/Tracking/Promotion/getPromotionEventTrackingData';

type NavigationLinksBlockProps = {
  block: NavigationLinksType;
};

export const InternalLinkCard = ({ link: { referenceComponent, text, _key } }: { link: CtaInternalLinkType }) => {
  const href = useDocumentUrlWithNode(referenceComponent);
  const promotion = getPromotionEventTrackingData(text, href);
  const shouldTrackPromotion = Boolean(promotion);
  const { wrapperRef, trackSelectPromotion } = useTrackPromotion({
    creative_slot: 'navigation-links',
    creative_name: `CardNavigation | ${text}`,
    promotion_id: promotion?.promotion_id,
    promotion_name: promotion?.promotion_name,
  });
  return (
    <StyledGridCol s="12" m="6" l="4" xl="4" key={`${_key}_${text}`}>
      <FlexWrapper ref={shouldTrackPromotion ? wrapperRef : undefined}>
        <NavCardLink
          to={href || '#'}
          onClick={() => {
            if (shouldTrackPromotion) {
              trackSelectPromotion();
            }
          }}
        >
          <NavCard isInternalLink title={text} height="100%" />
        </NavCardLink>
      </FlexWrapper>
    </StyledGridCol>
  );
};

export const ExternalLinkCard = ({ link: { link, text, _key, blank } }: { link: ExternalLinkType }) => {
  const promotion = getPromotionEventTrackingData(text, link);
  const shouldTrackPromotion = Boolean(promotion);
  const { wrapperRef, trackSelectPromotion } = useTrackPromotion({
    creative_slot: 'navigation-links',
    creative_name: `CardNavigation | ${text}`,
    promotion_id: promotion?.promotion_id,
    promotion_name: promotion?.promotion_name,
  });
  return (
    <StyledGridCol s="12" m="6" l="4" xl="4" key={`${_key}_${text}`}>
      <FlexWrapper
        ref={shouldTrackPromotion ? wrapperRef : undefined}
        onClick={() => {
          if (shouldTrackPromotion) {
            trackSelectPromotion();
          }
        }}
      >
        <NavCard href={link} target={blank ? '_blank' : '_self'} title={text || ''} height="100%" />
      </FlexWrapper>
    </StyledGridCol>
  );
};

export const NavigationLinksBlock = ({ block }: NavigationLinksBlockProps) => {
  if (block.links?.length > 0) {
    return (
      <Grid className="NavigationLinksBlock">
        <GridRow>
          {block.links.map((item) => {
            switch (item.type) {
              case 'ctaExternalLinkComponent':
                return <ExternalLinkCard key={`${item._key}`} link={item as unknown as ExternalLinkType} />;
              case 'ctaInternalLinkComponent':
                return <InternalLinkCard key={`${item._key}`} link={item as unknown as CtaInternalLinkType} />;
              default:
                return null;
            }
          })}
        </GridRow>
      </Grid>
    );
  }
  return null;
};

const StyledGridCol = styled(GridCol)`
  display: flex;
`;

/* The Simba card sizes to its content, so let it fill the grid cell. */
const FlexWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;

  > * {
    flex: 1;
  }
`;
